import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/download";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Loader2, Check, X, Star, ArrowLeft, Sparkles, Filter, Download, Flame, ArrowUpDown, MoreVertical,
} from "lucide-react";

const TIER = {
  hot: { label: "Hot", cls: "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]" },
  good: { label: "Good", cls: "bg-[#D1FAE5] text-[#047857] border-[#6EE7B7]" },
  maybe: { label: "Maybe", cls: "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]" },
  skip: { label: "Skip", cls: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]" },
};

const BRAND_CLS = {
  German: "bg-[#DBEAFE] text-[#1E40AF]",
  European: "bg-[#E0E7FF] text-[#4338CA]",
  Japanese: "bg-[#FEE2E2] text-[#B91C1C]",
  Korean: "bg-[#FCE7F3] text-[#BE185D]",
  American: "bg-[#FEF3C7] text-[#B45309]",
  general: "bg-[#F3F4F6] text-[#4B5563]",
  unknown: "bg-[#F3F4F6] text-[#9CA3AF]",
};

function ScoreBar({ score, tier }) {
  if (score == null) return <span className="text-xs font-bold text-[#9CA3AF] whitespace-nowrap">Not scored yet</span>;
  const t = TIER[tier] || TIER.skip;
  return (
    <div className="flex items-center gap-2">
      <div className="w-10 h-9 rounded-md bg-[#111827] text-white flex items-center justify-center text-sm font-black shrink-0">
        {score}
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase border ${t.cls}`}>{t.label}</span>
    </div>
  );
}

export default function ReviewPanel({ sweep, onBack, compact = false }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ areas: [], categories: [], total: 0, shown: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [classified, setClassified] = useState(sweep.classified);
  const [showFilters, setShowFilters] = useState(false);
  const [reviewMenuOpen, setReviewMenuOpen] = useState(false);
  const [f, setF] = useState({ tier: "", min_rating: "", min_reviews: "", area: "", category: "", status: "pending" });
  const [sort, setSort] = useState({ by: "score", dir: "desc" });

  const load = useCallback(async () => {
    try {
      const params = { sort_by: sort.by, sort_dir: sort.dir };
      Object.entries(f).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
      const [staging, sw] = await Promise.all([
        api.get(`/sweeps/${sweep.id}/staging`, { params }),
        api.get(`/sweeps/${sweep.id}`),
      ]);
      setRows(staging.data.rows);
      setMeta(staging.data);
      setClassified(sw.data.classified);
      setLoadError(null);
    } catch {
      setLoadError("Could not load these prospects.");
    }
    setLoading(false);
  }, [sweep.id, f, sort]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (classified) return;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [classified, load]);

  const selectable = rows.filter((r) => r.status === "pending");
  const allChecked = selectable.length > 0 && selectable.every((r) => checked[r.id]);
  const toggle = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const toggleAll = () => setChecked(allChecked ? {} : Object.fromEntries(selectable.map((r) => [r.id, true])));
  const selectGood = () => setChecked(Object.fromEntries(
    selectable.filter((r) => ["hot", "good"].includes(r.classification?.tier)).map((r) => [r.id, true])
  ));

  const act = async (path, ids) => {
    if (ids.length === 0) { toast.error("Select some prospects first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/staging/${path}`, { staging_ids: ids });
      toast.success(path === "approve" ? `${data.created} garages added` : "Rejected — won't reappear");
      setChecked({});
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const selectedIds = Object.keys(checked).filter((k) => checked[k]);
  const activeFilters = Object.entries(f).filter(([k, v]) => v !== "" && v != null && !(k === "status" && v === "pending")).length;
  const unscored = rows.filter((r) => (r.classification || {}).score == null).length;
  const [rescoring, setRescoring] = useState(false);

  const rescore = async () => {
    setRescoring(true);
    try {
      const { data } = await api.post(`/sweeps/${sweep.id}/rescore`);
      toast.success(data.queued ? `AI is scoring ${data.queued} prospects…` : data.message);
      setClassified(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRescoring(false); }
  };
  const setSortBy = (by) => setSort((p) => p.by === by ? { by, dir: p.dir === "asc" ? "desc" : "asc" } : { by, dir: "desc" });

  const Th = ({ col, children }) => (
    <th onClick={() => setSortBy(col)} data-testid={`review-sort-${col}`}
      className="px-3 py-2.5 text-left text-xs font-bold uppercase text-[#6B7280] cursor-pointer select-none hover:text-[#1E3A8A]">
      <span className="inline-flex items-center gap-1">{children}
        <ArrowUpDown className={`w-3 h-3 ${sort.by === col ? "text-[#1E3A8A]" : "opacity-40"}`} /></span>
    </th>
  );

  return (
    <div className="pb-24">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack} data-testid="review-back" className="p-2 rounded-md hover:bg-[#F3F4F6] shrink-0"><ArrowLeft className="w-5 h-5" /></button>
          <div className="min-w-0">
            <h2 className="text-xl font-black text-[#1E3A8A] truncate">{sweep.name}</h2>
            <p className="text-sm text-[#6B7280]">{meta.shown} of {meta.total} · best first</p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters"
            className="relative h-11 px-3 rounded-md border border-[#D1D5DB] font-bold text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filter
            {activeFilters > 0 && <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1E3A8A] text-white text-[10px] font-black flex items-center justify-center" data-testid="filter-badge">{activeFilters}</span>}
          </button>
          <Popover open={reviewMenuOpen} onOpenChange={setReviewMenuOpen}>
            <PopoverTrigger asChild>
              <button data-testid="review-menu" className="h-11 w-11 rounded-md border border-[#D1D5DB] flex items-center justify-center text-[#6B7280]"><MoreVertical className="w-5 h-5" /></button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1">
              <button onClick={() => { selectGood(); setReviewMenuOpen(false); }} data-testid="select-good" className="w-full flex items-center gap-2 px-3 h-11 rounded-md text-sm font-medium text-[#B91C1C] hover:bg-[#FEE2E2]"><Flame className="w-4 h-4" /> Select Hot + Good</button>
              <button onClick={() => { downloadCsv(`/export/prospects/${sweep.id}.csv`, `prospects-${sweep.name}.csv`); setReviewMenuOpen(false); }} data-testid="export-prospects" className="w-full flex items-center gap-2 px-3 h-11 rounded-md text-sm font-medium text-[#374151] hover:bg-[#F9FAFB]"><Download className="w-4 h-4" /> Export CSV</button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="fixed left-3 right-3 bottom-20 md:bottom-6 z-[500] bg-[#1E3A8A] text-white rounded-xl shadow-xl px-4 py-3 flex items-center justify-between gap-3" data-testid="review-bulk-bar">
          <span className="text-sm font-bold">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => act("reject", selectedIds)} disabled={busy} data-testid="reject-selected"
              className="h-10 px-3 rounded-lg bg-white/15 font-bold text-sm flex items-center gap-1.5"><X className="w-4 h-4" /> Reject</button>
            <button onClick={() => act("approve", selectedIds)} disabled={busy} data-testid="approve-selected"
              className="h-10 px-3 rounded-lg bg-white text-[#1E3A8A] font-bold text-sm flex items-center gap-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Add {selectedIds.length}</button>
          </div>
        </div>
      )}

      {showFilters && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 mb-4 grid grid-cols-2 md:grid-cols-6 gap-2">
          <select value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })} data-testid="filter-tier"
            className="h-11 px-2 rounded-md border border-[#D1D5DB] bg-white text-sm">
            <option value="">All tiers</option>
            {Object.entries(TIER).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={f.min_rating} onChange={(e) => setF({ ...f, min_rating: e.target.value })} data-testid="filter-min-rating"
            className="h-11 px-2 rounded-md border border-[#D1D5DB] bg-white text-sm">
            <option value="">Any rating</option>
            <option value="3.5">3.5★+</option><option value="4">4★+</option><option value="4.5">4.5★+</option>
          </select>
          <select value={f.min_reviews} onChange={(e) => setF({ ...f, min_reviews: e.target.value })} data-testid="filter-min-reviews"
            className="h-11 px-2 rounded-md border border-[#D1D5DB] bg-white text-sm">
            <option value="">Any reviews</option>
            <option value="10">10+ reviews</option><option value="50">50+ reviews</option><option value="150">150+ reviews</option>
          </select>
          <select value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} data-testid="filter-review-area"
            className="h-11 px-2 rounded-md border border-[#D1D5DB] bg-white text-sm">
            <option value="">All areas</option>
            {(meta.areas || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="filter-review-category"
            className="h-11 px-2 rounded-md border border-[#D1D5DB] bg-white text-sm">
            <option value="">All categories</option>
            {(meta.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="filter-review-status"
            className="h-11 px-2 rounded-md border border-[#D1D5DB] bg-white text-sm">
            <option value="pending">To review</option>
            <option value="">Everything</option>
            <option value="approved">Added</option>
            <option value="rejected">Rejected</option>
            <option value="in_db">Already in database</option>
          </select>
        </div>
      )}

      {!classified && (
        <div className="mb-4 flex items-center gap-2 text-sm font-medium text-[#6D28D9] bg-[#F3E8FF] border border-[#E9D5FF] rounded-md px-3 py-2">
          <Sparkles className="w-4 h-4 animate-pulse" /> AI is scoring the results… scores appear as they finish.
        </div>
      )}

      {loadError && (
        <div className="mb-4 flex items-center justify-between gap-2 text-sm font-medium text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-md px-3 py-2" data-testid="review-error">
          {loadError}
          <button onClick={load} className="h-9 px-3 rounded-md border border-[#FCA5A5] font-bold">Try again</button>
        </div>
      )}

      {classified && unscored > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm font-medium text-[#B45309] bg-[#FEF3C7] border border-[#FCD34D] rounded-md px-3 py-2">
          <span>Some of these were found before AI scoring existed, so they have no prospect score yet.</span>
          <button onClick={rescore} disabled={rescoring} data-testid="rescore-btn"
            className="h-9 px-3 rounded-md bg-[#B45309] text-white font-bold flex items-center gap-2 disabled:opacity-60">
            {rescoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Score them with AI
          </button>
        </div>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A8A]" /></div>
          : rows.length === 0 ? <p className="py-10 text-center text-[#6B7280]">Nothing matches these filters.</p>
          : rows.map((r) => {
            const c = r.classification || {};
            return (
              <div key={r.id} className={`bg-white border-2 rounded-xl p-3 ${checked[r.id] ? "border-[#1E3A8A]" : "border-[#E5E7EB]"} ${r.status !== "pending" ? "opacity-60" : ""}`}
                data-testid={`review-card-${r.id}`} onClick={() => r.status === "pending" && toggle(r.id)}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <ScoreBar score={c.score} tier={c.tier} />
                  {r.google_rating && <span className="text-xs text-[#6B7280] flex items-center gap-1"><Star className="w-3 h-3 text-[#F59E0B]" />{r.google_rating} ({r.google_review_count || 0})</span>}
                </div>
                <div className="font-bold text-[#111827]">{r.name}</div>
                <div className="text-xs text-[#6B7280]">{r.area || r.address}</div>
                {c.reason && <div className="text-xs text-[#374151] mt-1">{c.reason}</div>}
              </div>
            );
          })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="review-table">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>
                <th className="px-3 py-2.5 w-10"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="check-all" /></th>
                <Th col="score">Prospect score</Th>
                <Th col="name">Place</Th>
                <th className="px-3 py-2.5 text-left text-xs font-bold uppercase text-[#6B7280]">Why</th>
                <th className="px-3 py-2.5 text-left text-xs font-bold uppercase text-[#6B7280]">Brand</th>
                <Th col="rating">Google</Th>
                <th className="px-3 py-2.5 text-left text-xs font-bold uppercase text-[#6B7280]">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A8A] mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-[#6B7280]">Nothing matches these filters.</td></tr>
              ) : rows.map((r) => {
                const c = r.classification || {};
                return (
                  <tr key={r.id} className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] ${r.status !== "pending" ? "opacity-50" : ""}`} data-testid={`review-row-${r.id}`}>
                    <td className="px-3 py-2.5">
                      <input type="checkbox" disabled={r.status !== "pending"} checked={!!checked[r.id]} onChange={() => toggle(r.id)} data-testid={`check-${r.id}`} />
                    </td>
                    <td className="px-3 py-2.5"><ScoreBar score={c.score} tier={c.tier} /></td>
                    <td className="px-3 py-2.5">
                      <div className="font-bold text-[#111827] text-sm">{r.name}</div>
                      <div className="text-xs text-[#6B7280]">{r.area || r.address}</div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-[#374151] max-w-[280px]">{c.reason || "—"}</td>
                    <td className="px-3 py-2.5">
                      {c.brand_focus && <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${BRAND_CLS[c.brand_focus] || BRAND_CLS.unknown}`}>{c.brand_focus}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-[#374151]">
                      {r.google_rating ? <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#F59E0B]" /> {r.google_rating} ({r.google_review_count || 0})</span> : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-bold uppercase text-[#6B7280]">{r.status === "in_db" ? "In database" : r.status}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
